import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Chip
} from '@mui/material';
import {
  NoteAdd as NoteAddIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';

export type PlanRequestMode = 'scratch' | 'revise' | 'finish-incomplete' | 'pdf-upload';

interface ModeOption {
  mode: PlanRequestMode;
  title: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  disabled?: boolean;
  badge?: string;
}

interface PlanRequestModeSelectorProps {
  onSelectMode: (mode: PlanRequestMode) => void;
}

/**
 * Mode Selection Cards for Creating Plan Requests
 *
 * Displays 4 options in a 2x2 grid:
 * 1. Start from Scratch
 * 2. Revise Existing Plan
 * 3. Finish Incomplete Submission
 * 4. Start with PDF (Coming Soon)
 */
const PlanRequestModeSelector: React.FC<PlanRequestModeSelectorProps> = ({ onSelectMode }) => {
  const modes: ModeOption[] = [
    {
      mode: 'scratch',
      title: 'Start from Scratch',
      description: 'Create a brand new plan request with empty fields',
      icon: <NoteAddIcon sx={{ fontSize: 60 }} />,
      color: '#1976d2'
    },
    // Temporarily removed - revisions should be done from Client Detail page
    // {
    //   mode: 'revise',
    //   title: 'Revise Existing Plan',
    //   description: 'Modify an existing completed plan with pre-filled data',
    //   icon: <EditIcon sx={{ fontSize: 60 }} />,
    //   color: '#ed6c02'
    // },
    {
      mode: 'finish-incomplete',
      title: 'Finish Incomplete',
      description: 'Complete a partially submitted form from a client',
      icon: <PlayArrowIcon sx={{ fontSize: 60 }} />,
      color: '#2e7d32'
    },
    {
      mode: 'pdf-upload',
      title: 'Start with PDF',
      description: 'Upload an existing PDF to create a new plan request',
      icon: <UploadFileIcon sx={{ fontSize: 60 }} />,
      color: '#9c27b0',
      disabled: true,
      badge: 'Coming Soon'
    }
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
        How would you like to create this client?
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
        Select one of the options below to get started
      </Typography>

      <Grid container spacing={3}>
        {modes.map((option) => (
          <Grid item xs={12} sm={6} key={option.mode}>
            <Card
              elevation={option.disabled ? 0 : 2}
              sx={{
                height: '100%',
                opacity: option.disabled ? 0.5 : 1,
                position: 'relative',
                transition: 'all 0.2s',
                '&:hover': option.disabled ? {} : {
                  elevation: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardActionArea
                onClick={() => !option.disabled && onSelectMode(option.mode)}
                disabled={option.disabled}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent>
                  {/* Badge for disabled/special states */}
                  {option.badge && (
                    <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                      <Chip
                        label={option.badge}
                        size="small"
                        color="default"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                  )}

                  {/* Icon */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                      color: option.color
                    }}
                  >
                    {option.icon}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    align="center"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    {option.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    {option.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PlanRequestModeSelector;
